import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { parseServerError } from "utils/errors";


export const fetchLookUp = createAsyncThunk(
  "look-up/fetchLookUp",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/lookup", "POST", params);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchPeppolCountriesList = createAsyncThunk(
  "look-up/fetchPeppolCountriesList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/countries", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchPeppolDataRegistration= createAsyncThunk(
  "look-up/fetchPeppolDataRegistration",
  async (countryCode, { rejectWithValue }) => {
    try {
      // Build URL with country_code parameter if provided
      let url = "/peppol/data_registration"
      if (countryCode) {
        url += `?country_code=${countryCode}`
      }
      
      const response = await callApi(url, "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);


export const fetchAddPeppolNumber = createAsyncThunk(
  "look-up/fetchAddPeppolNumber",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol_number", "POST", params);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchGetPeppolNumber = createAsyncThunk(
  "look-up/fetchGetPeppolNumber",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol_number?is_active=false", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchAddPeppolRegistration = createAsyncThunk(
  "look-up/fetchAddPeppolRegistration",
  async (params, { rejectWithValue }) => {
    
    try {
      const response = await callApi("/peppol/registration", "POST", params);
      
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error message:', error.message);
      // Use rejectWithValue for all errors to prevent uncaught errors
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchPeppolVerification = createAsyncThunk(
  "look-up/fetchPeppolVerification",
  async ({ queryParams, formData }, { rejectWithValue }) => {
    try {
      // Build query string from queryParams
      const queryString = new URLSearchParams(queryParams).toString();
      const endpoint = `/peppol/verification?${queryString}`;
      
      const response = await callApi(endpoint, "POST", formData, {
        contentType: "multipart/form-data",
      });
      
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Verification error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchPeppolAgreement = createAsyncThunk(
  "look-up/fetchPeppolAgreement",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/agreement", "POST", params);

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Agreement error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchPeppolVerificationList = createAsyncThunk(
  "look-up/fetchPeppolVerificationList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/verification", "GET");
      
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Peppol verification list error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchInvoiceSettingsMessages = createAsyncThunk(
  "look-up/fetchInvoiceSettingsMessages",
  async (modelsName = 'invoice_settings', { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/notify/get_system_message_active?models_name=${modelsName}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return await response.json();
    } catch (error) {
      console.error('Invoice settings messages error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const updatePeppolVerification = createAsyncThunk(
  "look-up/updatePeppolVerification",
  async ({ verificationId, formData }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/peppol/verification/${verificationId}/edit`,
        "POST",
        formData,
        { contentType: "multipart/form-data" }
      );
      
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Peppol verification update error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const updatePeppolStatus = createAsyncThunk(
  "look-up/updatePeppolStatus",
  async ({ peppolId, statusData }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/peppol/verification/${peppolId}/status`, "PUT", statusData);

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Peppol status update error:', error.message);
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchEditPeppol = createAsyncThunk(
  "look-up/fetchEditPeppol",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/manager/peppol_number/edit", "POST", params);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchCountriesLanguage = createAsyncThunk(
  "look-up/CountriesLanguage",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents_settings/countries_language", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  "look-up/fetchLanguages",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/peppol/languages", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchAddLanguageForPDF = createAsyncThunk(
  "look-up/fetchAddLanguageForPDF",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents_settings/countries_language/add/", "POST", params);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchEditLanguageForPDF = createAsyncThunk(
  "look-up/fetchEditLanguageForPDF",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/documents_settings/countries_language/edit/", "POST", params);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchProductCountries = createAsyncThunk(
  "look-up/fetchProductCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/countries", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchProductTempFileData = createAsyncThunk(
  "look-up/fetchProductTempFileData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/temp-file-data", "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const addProductCountry = createAsyncThunk(
  "look-up/addProductCountry",
  async (countryId, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/countries", "POST", {
        country_id: countryId
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const deleteProductCountry = createAsyncThunk(
  "look-up/deleteProductCountry",
  async (countryCode, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/countries/${countryCode}`, "DELETE");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const upsertKsefToken = createAsyncThunk(
  "look-up/upsertKsefToken",
  async ({ country_id, ksef_token }, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/", "POST", {
        country_id,
        ksef_token
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const removeKsefToken = createAsyncThunk(
  "look-up/removeKsefToken",
  async (countryCode, { rejectWithValue }) => {
    try {
      const response = await callApi("/products/", "POST", {
        country_id: countryCode,
        ksef_token: ""
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "look-up/fetchProducts",
  async (countryCode, { rejectWithValue }) => {
    try {
      const url = countryCode ? `/products/?country_id=${countryCode}` : "/products/";
      const response = await callApi(url, "GET");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const addProduct = createAsyncThunk(
  "look-up/addProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const body = {
        country_id: productData.country_id || "",
        product_code: productData.product_code || "",
        sku: productData.sku || "",
        description: productData.description || "",
      };
      if (Object.prototype.hasOwnProperty.call(productData, "ksef_token")) {
        body.ksef_token = productData.ksef_token ?? "";
      }
      const response = await callApi("/products/", "POST", body);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const updateProduct = createAsyncThunk(
  "look-up/updateProduct",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const body = {
        country_id: productData.country_id || "",
        product_code: productData.product_code || "",
        sku: productData.sku || "",
        description: productData.description || "",
      };
      if (Object.prototype.hasOwnProperty.call(productData, "ksef_token")) {
        body.ksef_token = productData.ksef_token ?? "";
      }
      const response = await callApi(`/products/${productId}`, "PUT", body);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "look-up/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}`, "DELETE");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);
export const fetchTaxIDs = createAsyncThunk(
  "look-up/fetchTaxIDs",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/tax-ids", "GET");
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchTaxNumberTypes = createAsyncThunk(
  "look-up/fetchTaxNumberTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/tax-ids/types", "GET");
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const createTaxID = createAsyncThunk(
  "look-up/createTaxID",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/tax-ids/", "POST", params);
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const updateTaxID = createAsyncThunk(
  "look-up/updateTaxID",
  async (params, { rejectWithValue }) => {
    try {
      const { id, ...updateData } = params;
      const response = await callApi(`/tax-ids/${id}`, "PUT", updateData);
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const deleteTaxID = createAsyncThunk(
  "look-up/deleteTaxID",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/tax-ids/${id}`, "DELETE");
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchDeleteLanguageForPDF = createAsyncThunk(
  "look-up/fetchDeleteLanguageForPDF",
  async (id, { rejectWithValue }) => {
    try {
      // Don't pass body at all
      const response = await callApi(
        `/documents_settings/countries_language/delete/${id}`,
        "DELETE"
      );
      
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

export const fetchDeletePeppolNumber = createAsyncThunk(
  "look-up/fetchDeletePeppolNumber",
  async (id, { rejectWithValue }) => {
    try {
      // Don't pass body at all
      const response = await callApi(
        `/manager/peppol_number/${id}`,
        "DELETE"
      );
      
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);


const initialState = {
  look_up: [],
  countries_list: [],
  data_registration: {},
  peppol_number_list: [],
  countries_language: [],
  languages: [],
  tax_ids: [],
  fetchingTaxIDs: false,
  fetchingTaxIDsError: null,
  tax_number_types: [],
  fetchingTaxNumberTypes: false,
  fetchingTaxNumberTypesError: null,
  creatingTaxID: false,
  creatingTaxIDError: null,
  updatingTaxID: false,
  updatingTaxIDError: null,
  deletingTaxID: false,
  deletingTaxIDError: null,
  registrationMessage: null,
  // Peppol verification states
  fetchingPeppolVerification: false,
  fetchingPeppolVerificationError: null,
  verificationMessage: null,
  // Peppol agreement states
  fetchingPeppolAgreement: false,
  fetchingPeppolAgreementError: null,
  agreementDocumentUrl: null,
  // Peppol status update states
  updatingPeppolStatus: false,
  updatingPeppolStatusError: null,
  peppolStatusMessage: null,
  // Peppol verification list states
  fetchingPeppolVerificationList: false,
  fetchingPeppolVerificationListError: null,
  peppolVerificationList: [],
  // Invoice settings messages states
  fetchingInvoiceSettingsMessages: false,
  fetchingInvoiceSettingsMessagesError: null,
  invoiceSettingsMessages: [],
  // Peppol verification update states
  updatingPeppolVerification: false,
  updatingPeppolVerificationError: null,
  peppolVerificationUpdateMessage: null,

  fetchingAddPeppolNumberError: null,
  fetchingProductCountries: false,
  fetchingProductCountriesError: null,
  productCountries: [],
  fetchingProductTempFileData: false,
  fetchingProductTempFileDataError: null,
  productTempFileData: [],
  addingProductCountry: false,
  addingProductCountryError: null,
  upsertingKsefToken: false,
  upsertingKsefTokenError: null,
  removingKsefToken: false,
  removingKsefTokenError: null,
  deletingProductCountry: false,
  deletingProductCountryError: null,
  fetchingProducts: false,
  fetchingProductsError: null,
  products: [],
  addingProduct: false,
  addingProductError: null,
  updatingProduct: false,
  updatingProductError: null,
  deletingProduct: false,
  deletingProductError: null,
};

const lookUpSlice = createSlice({
  name: "look-up",
  initialState,
  reducers: {
    clearRegistrationMessage: (state) => {
      state.registrationMessage = null;
    },
    clearVerificationMessage: (state) => {
      state.verificationMessage = null;
    },
    clearPeppolStatusMessage: (state) => {
      state.peppolStatusMessage = null;
    },
    clearPeppolVerificationListMessage: (state) => {
      state.fetchingPeppolVerificationListError = null;
    },
    clearPeppolVerificationUpdateMessage: (state) => {
      state.peppolVerificationUpdateMessage = null;
    },
    clearFetchingAddPeppolNumberError: (state) => {
      state.fetchingAddPeppolNumberError = null;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchLookUp.pending, (state) => {
        state.fetchingLookUp = true;
      })
      .addCase(fetchLookUp.fulfilled, (state, action) => {
        state.fetchingLookUp = false;
        state.fetchingLookUpError = null;
        state.look_up = action.payload.companies;
      })
      .addCase(fetchLookUp.rejected, (state, action) => {
        state.fetchingLookUp = false;
        state.fetchingLookUpError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchPeppolCountriesList.pending, (state) => {
        state.fetchingPeppolCountriesList = true;
      })
      .addCase(fetchPeppolCountriesList.fulfilled, (state, action) => {
        state.fetchingPeppolCountriesList = false;
        state.fetchingPeppolCountriesListError = null;
        state.countries_list = action.payload;
      })
      .addCase(fetchPeppolCountriesList.rejected, (state, action) => {
        state.fetchingPeppolCountriesList = false;
        state.fetchingPeppolCountriesListError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchPeppolDataRegistration.pending, (state) => {
        state.fetchingPeppolDataRegistration = true;
      })
      .addCase(fetchPeppolDataRegistration.fulfilled, (state, action) => {
        state.fetchingPeppolDataRegistration = false;
        state.fetchingPeppolDataRegistrationError = null;
        state.data_registration = action.payload;
      })
      .addCase(fetchPeppolDataRegistration.rejected, (state, action) => {
        state.fetchingPeppolDataRegistration = false;
        state.fetchingPeppolDataRegistrationError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchAddPeppolNumber.pending, (state) => {
        state.fetchingAddPeppolNumber = true;
      })
      .addCase(fetchAddPeppolNumber.fulfilled, (state, action) => {
        state.fetchingAddPeppolNumber = false;
        state.fetchingAddPeppolNumberError = action?.payload?.error || null;
      })
      .addCase(fetchAddPeppolNumber.rejected, (state, action) => {
        state.fetchingAddPeppolNumber = false;
        state.fetchingAddPeppolNumberError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase('CLEAR_REGISTRATION_MESSAGE', (state) => {
        state.registrationMessage = null;
      })
      .addCase(fetchAddPeppolRegistration.pending, (state) => {
        state.fetchingAddPeppolRegistration = true;
      })
             .addCase(fetchAddPeppolRegistration.fulfilled, (state, action) => {
         state.fetchingAddPeppolRegistration = false;
         state.fetchingAddPeppolRegistrationError = null;
         
         // Check if there's a successful response with Peppol ID
         if (action.payload?.peppol_response?.["Peppol ID"]) {
           const peppolId = action.payload.peppol_response["Peppol ID"];
           state.registrationMessage = {
             success: `Peppol ID: ${peppolId}`,
             documentUrl: action.payload.document_url
           };
         } else {
           // Fallback to original payload if structure is different
           state.registrationMessage = action.payload;
         }
       })
      .addCase(fetchAddPeppolRegistration.rejected, (state, action) => {
        state.fetchingAddPeppolRegistration = false;
        state.fetchingAddPeppolRegistrationError = parseServerError(action.payload ?? action.error);
        
        // Store error message for display in component
        state.registrationMessage = {
          error: action.payload || action.error?.message || "Registration failed"
        };

      })
      
      .addCase(fetchGetPeppolNumber.pending, (state) => {
        state.fetchingGetPeppolNumber = true;
      })
      .addCase(fetchGetPeppolNumber.fulfilled, (state, action) => {
        state.fetchingGetPeppolNumber = false;
        state.fetchingGetPeppolNumberError = null;
        state.peppol_number_list = action.payload
      })
      .addCase(fetchGetPeppolNumber.rejected, (state, action) => {
        state.fetchingGetPeppolNumber = false;
        state.fetchingGetPeppolNumberError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchEditPeppol.pending, (state) => {
        state.fetchingEditPeppol = true;
      })
      .addCase(fetchEditPeppol.fulfilled, (state, action) => {
        state.fetchingEditPeppol = false;
        state.fetchingEditPeppolError = null;
      })
      .addCase(fetchEditPeppol.rejected, (state, action) => {
        state.fetchingEditPeppol = false;
        state.fetchingEditPeppolError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchCountriesLanguage.pending, (state) => {
        state.fetchingCountriesLanguage = true;
      })
      .addCase(fetchCountriesLanguage.fulfilled, (state, action) => {
        state.fetchingCountriesLanguage = false;
        state.fetchingCountriesLanguageError = null;
        state.countries_language = action.payload
      })
      .addCase(fetchCountriesLanguage.rejected, (state, action) => {
        state.fetchingCountriesLanguage = false;
        state.fetchingCountriesLanguageError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchLanguages.pending, (state) => {
        state.fetchingLanguages = true;
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.fetchingLanguages = false;
        state.fetchingLanguagesError = null;
        state.languages = action.payload
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.fetchingLanguages = false;
        state.fetchingLanguagesError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchAddLanguageForPDF.pending, (state) => {
        state.fetchingAddLanguageForPDF = true;
      })
      .addCase(fetchAddLanguageForPDF.fulfilled, (state, action) => {
        state.fetchingAddLanguageForPDF = false;
        state.fetchingAddLanguageForPDFError = null;
      })
      .addCase(fetchAddLanguageForPDF.rejected, (state, action) => {
        state.fetchingAddLanguageForPDF = false;
        state.fetchingAddLanguageForPDFError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchEditLanguageForPDF.pending, (state) => {
        state.fetchingEditLanguageForPDF = true;
      })
      .addCase(fetchEditLanguageForPDF.fulfilled, (state, action) => {
        state.fetchingEditLanguageForPDF = false;
        state.fetchingEditLanguageForPDFError = null;
      })
      .addCase(fetchEditLanguageForPDF.rejected, (state, action) => {
        state.fetchingEditLanguageForPDF = false;
        state.fetchingEditLanguageForPDFError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchDeleteLanguageForPDF.pending, (state) => {
        state.fetchingDeleteLanguageForPDF = true;
      })
      .addCase(fetchDeleteLanguageForPDF.fulfilled, (state, action) => {
        state.fetchingDeleteLanguageForPDF = false;
        state.fetchingDeleteLanguageForPDFError = null;
      })
      .addCase(fetchDeleteLanguageForPDF.rejected, (state, action) => {
        state.fetchingDeleteLanguageForPDF = false;
        state.fetchingDeleteLanguageForPDFError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchTaxIDs.pending, (state) => {
        state.fetchingTaxIDs = true;
      })
      .addCase(fetchTaxIDs.fulfilled, (state, action) => {
        state.fetchingTaxIDs = false;
        state.fetchingTaxIDsError = null;
        state.tax_ids = action.payload?.tax_ids;
      })
      .addCase(fetchTaxIDs.rejected, (state, action) => {
        state.fetchingTaxIDs = false;
        state.fetchingTaxIDsError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchTaxNumberTypes.pending, (state) => {
        state.fetchingTaxNumberTypes = true;
        state.fetchingTaxNumberTypesError = null;
      })
      .addCase(fetchTaxNumberTypes.fulfilled, (state, action) => {
        state.fetchingTaxNumberTypes = false;
        state.fetchingTaxNumberTypesError = null;
        state.tax_number_types = action.payload?.types || action.payload || [];
      })
      .addCase(fetchTaxNumberTypes.rejected, (state, action) => {
        state.fetchingTaxNumberTypes = false;
        state.fetchingTaxNumberTypesError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(createTaxID.pending, (state) => {
        state.creatingTaxID = true;
        state.creatingTaxIDError = null;
      })
      .addCase(createTaxID.fulfilled, (state, action) => {
        state.creatingTaxID = false;
        state.creatingTaxIDError = null;
      })
      .addCase(createTaxID.rejected, (state, action) => {
        state.creatingTaxID = false;
        state.creatingTaxIDError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(updateTaxID.pending, (state) => {
        state.updatingTaxID = true;
        state.updatingTaxIDError = null;
      })
      .addCase(updateTaxID.fulfilled, (state, action) => {
        state.updatingTaxID = false;
        state.updatingTaxIDError = null;
      })
      .addCase(updateTaxID.rejected, (state, action) => {
        state.updatingTaxID = false;
        state.updatingTaxIDError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(deleteTaxID.pending, (state) => {
        state.deletingTaxID = true;
        state.deletingTaxIDError = null;
      })
      .addCase(deleteTaxID.fulfilled, (state, action) => {
        state.deletingTaxID = false;
        state.deletingTaxIDError = null;
      })
      .addCase(deleteTaxID.rejected, (state, action) => {
        state.deletingTaxID = false;
        state.deletingTaxIDError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchDeletePeppolNumber.pending, (state) => {
        state.fetchingDeletePeppolNumber = true;
      })
      .addCase(fetchDeletePeppolNumber.fulfilled, (state, action) => {
        state.fetchingDeletePeppolNumber = false;
        state.fetchingDeletePeppolNumberError = null;
      })
      .addCase(fetchDeletePeppolNumber.rejected, (state, action) => {
        state.fetchingDeletePeppolNumber = false;
        state.fetchingDeletePeppolNumberError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchPeppolVerification.pending, (state) => {
        state.fetchingPeppolVerification = true;
        state.verificationMessage = null;
      })
      .addCase(fetchPeppolVerification.fulfilled, (state, action) => {
        state.fetchingPeppolVerification = false;
        state.fetchingPeppolVerificationError = null;
        state.verificationMessage = {
          success: true,
          message: "Verification completed successfully"
        };
      })
      .addCase(fetchPeppolVerification.rejected, (state, action) => {
        state.fetchingPeppolVerification = false;
        state.fetchingPeppolVerificationError = parseServerError(action.payload ?? action.error);
        state.verificationMessage = {
          error: action.payload || action.error?.message || "Verification failed"
        };
      })
      
      .addCase(fetchPeppolAgreement.pending, (state) => {
        state.fetchingPeppolAgreement = true;
        state.agreementDocumentUrl = null;
      })
      .addCase(fetchPeppolAgreement.fulfilled, (state, action) => {
        state.fetchingPeppolAgreement = false;
        state.fetchingPeppolAgreementError = null;
        // Extract document_url from API response
        state.agreementDocumentUrl = action.payload?.document_url || action.payload?.url || action.payload;
      })
      .addCase(fetchPeppolAgreement.rejected, (state, action) => {
        state.fetchingPeppolAgreement = false;
        state.fetchingPeppolAgreementError = parseServerError(action.payload ?? action.error);
        state.agreementDocumentUrl = null;
      })
      
      .addCase(updatePeppolStatus.pending, (state) => {
        state.updatingPeppolStatus = true;
        state.peppolStatusMessage = null;
      })
      .addCase(updatePeppolStatus.fulfilled, (state, action) => {
        state.updatingPeppolStatus = false;
        state.updatingPeppolStatusError = null;
        state.peppolStatusMessage = {
          success: true,
          message: "Peppol status updated successfully"
        };
        state.peppolVerificationList = action.payload;
      })
      .addCase(updatePeppolStatus.rejected, (state, action) => {
        state.updatingPeppolStatus = false;
        state.updatingPeppolStatusError = parseServerError(action.payload ?? action.error);
        state.peppolStatusMessage = {
          error: parseServerError(action.payload ?? action.error)
        };
      })
      
      .addCase(fetchPeppolVerificationList.pending, (state) => {
        state.fetchingPeppolVerificationList = true;
        state.fetchingPeppolVerificationListError = null;
      })
      .addCase(fetchPeppolVerificationList.fulfilled, (state, action) => {
        state.fetchingPeppolVerificationList = false;
        state.fetchingPeppolVerificationListError = null;
        state.peppolVerificationList = action.payload;
      })
      .addCase(fetchPeppolVerificationList.rejected, (state, action) => {
        state.fetchingPeppolVerificationList = false;
        state.fetchingPeppolVerificationListError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchInvoiceSettingsMessages.pending, (state) => {
        state.fetchingInvoiceSettingsMessages = true;
        state.fetchingInvoiceSettingsMessagesError = null;
      })
      .addCase(fetchInvoiceSettingsMessages.fulfilled, (state, action) => {
        state.fetchingInvoiceSettingsMessages = false;
        state.fetchingInvoiceSettingsMessagesError = null;
        state.invoiceSettingsMessages = action.payload;
      })
      .addCase(fetchInvoiceSettingsMessages.rejected, (state, action) => {
        state.fetchingInvoiceSettingsMessages = false;
        state.fetchingInvoiceSettingsMessagesError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(updatePeppolVerification.pending, (state) => {
        state.updatingPeppolVerification = true;
        state.peppolVerificationUpdateError = null;
      })
      .addCase(updatePeppolVerification.fulfilled, (state, action) => {
        state.updatingPeppolVerification = false;
        state.peppolVerificationUpdateError = null;
        state.peppolVerificationUpdateMessage = {
          success: true,
          message: "Files uploaded successfully"
        };
      })
      .addCase(updatePeppolVerification.rejected, (state, action) => {
        state.updatingPeppolVerification = false;
        state.peppolVerificationUpdateError = parseServerError(action.payload ?? action.error);
        state.peppolVerificationUpdateMessage = {
          error: parseServerError(action.payload ?? action.error)
        };
      })
      
      .addCase(fetchProductCountries.pending, (state) => {
        state.fetchingProductCountries = true;
        state.fetchingProductCountriesError = null;
      })
      .addCase(fetchProductCountries.fulfilled, (state, action) => {
        state.fetchingProductCountries = false;
        state.fetchingProductCountriesError = null;
        state.productCountries = action.payload?.countries || [];
      })
      .addCase(fetchProductCountries.rejected, (state, action) => {
        state.fetchingProductCountries = false;
        state.fetchingProductCountriesError = parseServerError(action.payload ?? action.error);
        state.productCountries = [];
      })
      
      .addCase(fetchProductTempFileData.pending, (state) => {
        state.fetchingProductTempFileData = true;
        state.fetchingProductTempFileDataError = null;
      })
      .addCase(fetchProductTempFileData.fulfilled, (state, action) => {
        state.fetchingProductTempFileData = false;
        state.fetchingProductTempFileDataError = null;
        state.productTempFileData = action.payload?.data;
      })
      .addCase(fetchProductTempFileData.rejected, (state, action) => {
        state.fetchingProductTempFileData = false;
        state.fetchingProductTempFileDataError = parseServerError(action.payload ?? action.error);
        state.productTempFileData = [];
      })
      
      .addCase(addProductCountry.pending, (state) => {
        state.addingProductCountry = true;
        state.addingProductCountryError = null;
      })
      .addCase(addProductCountry.fulfilled, (state, action) => {
        state.addingProductCountry = false;
        state.addingProductCountryError = null;
      })
      .addCase(addProductCountry.rejected, (state, action) => {
        state.addingProductCountry = false;
        state.addingProductCountryError = parseServerError(action.payload ?? action.error);
      })

      .addCase(upsertKsefToken.pending, (state) => {
        state.upsertingKsefToken = true;
        state.upsertingKsefTokenError = null;
      })
      .addCase(upsertKsefToken.fulfilled, (state) => {
        state.upsertingKsefToken = false;
        state.upsertingKsefTokenError = null;
      })
      .addCase(upsertKsefToken.rejected, (state, action) => {
        state.upsertingKsefToken = false;
        state.upsertingKsefTokenError = parseServerError(action.payload ?? action.error);
      })

      .addCase(removeKsefToken.pending, (state) => {
        state.removingKsefToken = true;
        state.removingKsefTokenError = null;
      })
      .addCase(removeKsefToken.fulfilled, (state) => {
        state.removingKsefToken = false;
        state.removingKsefTokenError = null;
      })
      .addCase(removeKsefToken.rejected, (state, action) => {
        state.removingKsefToken = false;
        state.removingKsefTokenError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(deleteProductCountry.pending, (state) => {
        state.deletingProductCountry = true;
        state.deletingProductCountryError = null;
      })
      .addCase(deleteProductCountry.fulfilled, (state, action) => {
        state.deletingProductCountry = false;
        state.deletingProductCountryError = null;
      })
      .addCase(deleteProductCountry.rejected, (state, action) => {
        state.deletingProductCountry = false;
        state.deletingProductCountryError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(fetchProducts.pending, (state) => {
        state.fetchingProducts = true;
        state.fetchingProductsError = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = null;
        state.products = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = parseServerError(action.payload ?? action.error);
        state.products = [];
      })
      
      .addCase(addProduct.pending, (state) => {
        state.addingProduct = true;
        state.addingProductError = null;
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.addingProduct = false;
        state.addingProductError = null;
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.addingProduct = false;
        state.addingProductError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(updateProduct.pending, (state) => {
        state.updatingProduct = true;
        state.updatingProductError = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.updatingProduct = false;
        state.updatingProductError = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.updatingProduct = false;
        state.updatingProductError = parseServerError(action.payload ?? action.error);
      })
      
      .addCase(deleteProduct.pending, (state) => {
        state.deletingProduct = true;
        state.deletingProductError = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = parseServerError(action.payload ?? action.error);
      })
});

export const { 
  clearRegistrationMessage, 
  clearVerificationMessage, 
  clearPeppolStatusMessage, 
  clearPeppolVerificationListMessage, 
  clearPeppolVerificationUpdateMessage,
  clearFetchingAddPeppolNumberError
} = lookUpSlice.actions;
export default lookUpSlice.reducer;
